import {
  GridColDef,
  GridRowSelectionModel
} from '@mui/x-data-grid-pro'
import {
  BookingsPaginatedQuery, PaginationInput, useBookingsPaginatedQuery
} from '@typings/graphql'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { VOUCHER_GRID_BASE_ORDER_FILTER } from '@features/voucher/constants/constants'
import BaseDataGrid from '@shared/components/dataGrid/BaseDataGrid'
import { DATA_GRID_PAGE_SIZE } from '@shared/components/dataGrid/constants/dataGrid'

import { useVoucherContext } from '../../../provider/VoucherProvider'

import { dataGridColumns } from './dataGridColumns'

type Props = {
  onVoucherSelected: (voucher: GridRowSelectionModel) => void
}

export type PaginatedBookingNode = Exclude<BookingsPaginatedQuery['bookingsPaginated']['nodes'], null | undefined>[0]
export type PaginatedBookingNodePayment = Exclude<PaginatedBookingNode['payment'], null | undefined>
export type PaginatedBookingRowModel = Omit<PaginatedBookingNode, '__typename' | 'payment'>

export type PaginatedBookingColumnType = Omit<GridColDef<PaginatedBookingRowModel>, 'field'> & {
  field: keyof PaginatedBookingRowModel,
}

export const VoucherDataGrid: React.FC<Props> = ({ onVoucherSelected }) => {
  const { t } = useTranslation()
  const { selectedTour } = useVoucherContext()

  const { data, loading, refetch } = useBookingsPaginatedQuery({
    variables: {
      tourId: selectedTour?.id as string,
      data: {
        limit: DATA_GRID_PAGE_SIZE,
        offset: 0,
        ...VOUCHER_GRID_BASE_ORDER_FILTER
      }
    },
    skip: !selectedTour?.id
  })

  const columnData = React.useMemo<PaginatedBookingColumnType[]>(() => {
    return dataGridColumns.map((column) => {
      return {
        headerName: t(`payment.${column.field}`),
        disableColumnMenu: true,
        ...column
      }
    })
  }, [])

  const mappedRows = React.useMemo<PaginatedBookingRowModel[]>(() => {
    return data?.bookingsPaginated.nodes?.map((node) => ({
      id: node.code,
      ...node
    }) as PaginatedBookingRowModel) || []
  }, [data])

  const handleFetchMore = React.useCallback(async (variable: PaginationInput) => {
    return await refetch({
      data: {
        ...variable,
        orderBy: variable.orderBy || VOUCHER_GRID_BASE_ORDER_FILTER.orderBy,
        filter: [...(variable.filter || []), ...VOUCHER_GRID_BASE_ORDER_FILTER.filter]
      }
    })
  }, [refetch])

  return (
    <BaseDataGrid
      loading={loading}
      gridKey='user'
      columns={columnData}
      serverSide
      refetch={handleFetchMore}
      onRowSelectionModelChange={(model) => onVoucherSelected(model)}
      pageInfo={data?.bookingsPaginated.pageInfo}
      totalRows={data?.bookingsPaginated.totalCount}
      rows={mappedRows}
    />

  )
}
